/**
 * career-applications — the applications list + generation launcher (route `/applications`).
 *
 * Shows the owner's applications (fetch-reconciled from `/api/applications`), a GENERATE affordance
 * (pick a matched job → `/api/applications/generate` → the detached run authors the letter), and the
 * LIVE push rail (the GenerationRun store folded from the push plane, `role="log" aria-live="polite"`,
 * stage + review-round counts with meaning). Each application card carries its `is-<status>` state and
 * an `open-application` verb. All chrome flows through `t()`; letter/kurzprofil CONTENT is domain data.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import {
  ApplicationStoreToken,
  GenerationRunStoreToken,
  I18nToken,
  PushToken,
  RouterToken,
  SessionToken,
  apiGet,
  apiPost,
} from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import {
  applicationStatusClass,
  careerStream,
  type ApplicationStore,
  type ApplicationView,
  type GenerationRunStage,
  type GenerationRunStatus,
  type GenerationRunStore,
  type GenerationRunView,
  type ShellSession,
} from "../domain"

interface OpportunityWithMatch {
  opportunity: { id?: string; posting?: { employer?: string; title?: string } }
  match: unknown
}
interface GenerateResponse {
  applicationId: string
  generationId: string
  streamId: string
  applicationStreamId: string
}
interface PickOpp {
  shortId: string
  employer: string
  title: string
}

const STAGE_KEY: Record<GenerationRunStage, CareerKey> = {
  preselecting: "applications.gen.stage.preselecting",
  authoring: "applications.gen.stage.authoring",
  judging: "applications.gen.stage.judging",
  kurzprofil: "applications.gen.stage.kurzprofil",
  tailoring: "applications.gen.stage.tailoring",
  emailing: "applications.gen.stage.emailing",
  done: "applications.gen.stage.done",
}
const STATUS_KEY: Record<GenerationRunStatus, CareerKey> = {
  running: "applications.gen.status.running",
  completed: "applications.gen.status.completed",
  stopped: "applications.gen.status.stopped",
  failed: "applications.gen.status.failed",
}
const APP_STATUS_KEY: Record<string, CareerKey> = {
  draft: "applications.status.draft",
  in_review: "applications.status.in_review",
  changes_requested: "applications.status.changes_requested",
  approved: "applications.status.approved",
  sent: "applications.status.sent",
}

const POLL_MS = 2500

export class ApplicationsVm implements ViewModel {
  readonly applications: ReadonlySignal<readonly ApplicationView[]>
  readonly runs: ReadonlySignal<readonly GenerationRunView[]>
  readonly opportunities: Signal<readonly PickOpp[]> = signal<readonly PickOpp[]>([])
  readonly selected: Signal<string | null> = signal<string | null>(null)
  readonly generating: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  private readonly streams = new Set<string>()
  private seq = 1
  private timer: ReturnType<typeof setInterval> | null = null

  constructor(
    private readonly store: ApplicationStore,
    private readonly genStore: GenerationRunStore,
    private readonly push: PushChannel,
    private readonly router: Router,
    private readonly session: ShellSession,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.applications = store.list("alle")
    this.runs = genStore.list("aktiv")
    this.renderDeps = [this.applications, this.runs, this.opportunities, this.selected, this.generating, this.error, this.loaded, store.pending]
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    void this.hydrate()
    return () => this.stopPolling()
  }

  isRunning(): boolean {
    return this.runs.value.some((r) => r.status === "running")
  }

  select(shortId: string): void {
    this.selected.set(shortId)
  }

  async generate(): Promise<void> {
    const shortId = this.selected.value
    if (!shortId) return
    this.generating.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<GenerateResponse>("/api/applications/generate", { opportunityId: shortId })
      this.streams.add(res.streamId)
      this.streams.add(res.applicationStreamId)
      this.push.subscribe([...this.streams]) // live: gen rail + application content
      await this.hydrate()
      this.startPolling()
      // Jump straight to the review surface — the rail plays there too.
      const appShort = res.applicationId.replace(/^urn:career:application:/, "")
      void this.router.navigate(`/applications/${appShort}`)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.generating.set(false)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  private startPolling(): void {
    this.stopPolling()
    this.timer = setInterval(() => {
      void this.hydrate().then(() => {
        if (!this.isRunning()) this.stopPolling()
      })
    }, POLL_MS)
  }
  private stopPolling(): void {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  /** Reconcile the owner's applications + the pickable opportunities. */
  private async hydrate(): Promise<void> {
    const userId = this.userId
    try {
      const apps = await apiGet<Record<string, unknown>[]>("/api/applications")
      if (userId) {
        for (const app of apps) {
          const id = app["id"]
          if (typeof id !== "string") continue
          // Seed/refresh the store via a synthetic Drafted carrying the FULL folded application.
          this.store.apply({ streamId: careerStream(userId, "Application", id), version: this.seq++, type: "application.Drafted", payload: { owner: userId, application: app } })
        }
      }
      const opps = await apiGet<OpportunityWithMatch[]>("/api/opportunities")
      const pick: PickOpp[] = opps
        .map((o) => o.opportunity)
        .filter((o): o is { id: string; posting?: { employer?: string; title?: string } } => typeof o.id === "string")
        .map((o) => ({ shortId: o.id.replace(/^urn:career:opportunity:/, ""), employer: o.posting?.employer ?? "", title: o.posting?.title ?? "" }))
      this.opportunities.set(pick)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }
}

export class CareerApplications extends KitElement<ApplicationsVm> {
  protected createViewModel(ctx: KitContext): ApplicationsVm {
    return new ApplicationsVm(
      ctx.inject(ApplicationStoreToken),
      ctx.inject(GenerationRunStoreToken),
      ctx.inject(PushToken),
      ctx.inject(RouterToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
    )
  }

  private picker(vm: ApplicationsVm): TemplateResult {
    const opps = vm.opportunities.value
    return html`
      <section class="applications-generate surface-panel" aria-labelledby="applications-generate-title">
        <h2 id="applications-generate-title" class="surface-section-title" lang="de">${vm.t("applications.pick.title")}</h2>
        ${opps.length === 0
          ? html`<p class="surface-empty" lang="de">${vm.t("applications.pick.none")}</p>`
          : html`
              <div class="applications-picker" role="group" aria-label=${vm.t("applications.pick.title")}>
                <ul class="applications-pick-list" role="list">
                  ${repeat(opps, (o) => o.shortId, (o) => html`
                    <li>
                      <button
                        data-act="select-opportunity"
                        data-opportunity=${o.shortId}
                        class=${classMap({ "applications-pick": true, "is-selected": vm.selected.value === o.shortId })}
                        aria-pressed=${vm.selected.value === o.shortId}
                        lang="de"
                      >${o.title}${o.employer ? ` · ${o.employer}` : ""}</button>
                    </li>`)}
                </ul>
                <button
                  data-act="generate-application"
                  data-opportunity=${vm.selected.value ?? ""}
                  class=${classMap({ "applications-generate-btn": true, "is-pending": vm.generating.value, "is-x-prominent": true })}
                  ?disabled=${!vm.selected.value || vm.generating.value}
                  lang="de"
                >${vm.generating.value ? vm.t("applications.generating") : vm.t("applications.generate")}</button>
              </div>`}
      </section>
    `
  }

  private railRun(vm: ApplicationsVm, run: GenerationRunView): TemplateResult {
    return html`
      <li class=${classMap({ "generation-run": true, [`is-${run.status}`]: true })} data-run-id=${run.urn}>
        <span class="run-stage chip" lang="de">${vm.t(STAGE_KEY[run.stage])}</span>
        <span class="run-status" lang="de">${vm.t(STATUS_KEY[run.status])}</span>
        ${run.iterations > 0 ? html`<span class="run-iterations chip" lang="de">${vm.t("applications.gen.iterations", { n: run.iterations })}</span>` : nothing}
      </li>
    `
  }

  private card(vm: ApplicationsVm, a: ApplicationView): TemplateResult {
    const passed = a.gates.filter((g) => g.pass).length
    return html`
      <li class=${classMap({ "application-card": true, [applicationStatusClass(a.status)]: true, "is-degraded": a.letterDegraded })} data-application=${a.urn} urn=${a.urn}>
        <p class="application-card-status chip" lang="de">${vm.t(APP_STATUS_KEY[a.status] ?? "applications.status.draft")}</p>
        ${a.gates.length > 0 ? html`<p class="application-card-gates chip" lang="de">${passed}/${a.gates.length} · ${a.converged ? vm.t("applications.gen.converged") : vm.t("applications.gen.notConverged")}</p>` : nothing}
        ${a.letterBody ? html`<p class="application-card-snippet" lang="de">${a.letterBody.split("\n\n")[0]}</p>` : nothing}
        <button data-act="open-application" data-application=${a.id.replace(/^urn:career:application:/, "")} class="application-open" lang="de">${vm.t("applications.open")}</button>
      </li>
    `
  }

  protected render(vm: ApplicationsVm): TemplateResult {
    const apps = vm.applications.value
    const runs = vm.runs.value
    const err = vm.error.value
    return html`
      <section class="surface applications" aria-labelledby="applications-title">
        <h1 id="applications-title" class="surface-title" lang="de">${vm.t("surface.bewerbungen.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("surface.bewerbungen.lead")}</p>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

        ${this.picker(vm)}

        <section class="applications-rail-section" aria-labelledby="applications-rail-title">
          <h2 id="applications-rail-title" class="surface-section-title" lang="de">${vm.t("applications.rail.title")}</h2>
          <ol class="applications-rail" role="log" aria-live="polite" aria-label=${vm.t("applications.rail.title")}>
            ${runs.length === 0
              ? html`<li class="applications-rail-empty" lang="de">${vm.t("applications.rail.empty")}</li>`
              : repeat(runs, (r) => r.urn, (r) => this.railRun(vm, r))}
          </ol>
        </section>

        <section class="applications-list-section" aria-labelledby="applications-list-title" aria-live="polite">
          <h2 id="applications-list-title" class="surface-section-title" lang="de">${vm.t("applications.list.title")}</h2>
          ${apps.length === 0
            ? html`<p class="surface-empty" lang="de">${vm.loaded.value ? vm.t("applications.empty") : ""}</p>`
            : html`<ul class="application-list" role="list">${repeat(apps, (a) => a.urn, (a) => this.card(vm, a))}</ul>`}
        </section>
      </section>
    `
  }

  protected override actions(vm: ApplicationsVm): ActionMap {
    return {
      "select-opportunity": ({ opportunity }) => {
        if (opportunity) vm.select(opportunity)
      },
      "generate-application": () => void vm.generate(),
      "open-application": ({ application }, event) => {
        event.preventDefault()
        if (application) vm.go(`/applications/${application}`)
      },
    }
  }
}
